import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {interval, Observable} from 'rxjs';
import {map} from 'rxjs/internal/operators/map';
import {FIX_VERSION, PATCH_VERSION} from './consts/patch-version-consts';
import {PrlAuthenticationService} from './shared/services/prl-authentication.service';
import {PrlLanguageService} from './view/prl-language/services/prl-language.service';
import {MxPreloaderService} from '../../../mx-core/src/lib/modules/mx-preloader/services/mx-preloader.service';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {MatSidenav} from '@angular/material/sidenav';

@Component({
    selector: 'prl-app-root',
    standalone: false,
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
    @ViewChild('snav')
    private sidenav: MatSidenav;

    serverTime: Observable<number>;
    version: string = PATCH_VERSION + '.' + FIX_VERSION;
    portalLogo: string;

    protected sidenavMode: 'side' | 'over' = 'side';
    protected sidenavOpened = true;

    private screenWidth: number;

    constructor(
        private readonly breakpointObserver: BreakpointObserver,
        public readonly authService: PrlAuthenticationService,
        protected readonly languageService: PrlLanguageService,
        private readonly route: ActivatedRoute,
        private readonly preloader: MxPreloaderService,
        private readonly router: Router)
    {
        this.languageService.init();
        this.getScreenSize();
    }

    @HostListener('window:resize', ['$event'])
    getScreenSize() {
        this.screenWidth = window.innerWidth;
    }

    ngOnInit(): void {
        this.breakpointObserver
            .observe([Breakpoints.Handset])
            .subscribe(result => {
                if (result.matches) {
                    this.sidenavMode = 'over';
                    this.sidenavOpened = false;
                } else {
                    this.sidenavMode = 'side';
                    this.sidenavOpened = true;
                }
            });

        let os = this.getOs();
        const appRoot = document.getElementById('app-root');
        appRoot.classList.add(os);
        this.authService.handleLoginFromBo();
        this.authService.checkAuth();
        this.serverTime = interval(1000)
            .pipe(map(() => Date.now()));
    }

    isLoginPage = (): boolean => {
        return this.router.url === '/login';
    };

    isTermsPage = (): boolean => {
        return this.router.url.startsWith('/terms');
    };

    isPortalNotificationPage = (): boolean => {
        return this.router.url.startsWith('/portal-notifications');
    };

    isForceResetRequired = (): boolean => {
        return this.authService.isForceResetRequired();
    };

    isChangePasswordPage = (): boolean => {
        return this.router.url.startsWith('/force-reset-password') ||
            this.router.url.startsWith('/reset-password/');
    };

    getOs = (): string => {
        if (navigator.appVersion.lastIndexOf('Win') !== -1) {
            return 'windows';
        }
        if (navigator.appVersion.lastIndexOf('Mac') !== -1) {
            return 'mac';
        }
        if (navigator.appVersion.lastIndexOf('Linux') !== -1) {
            return 'linux';
        }
    };

    getLabel = (label: string): string => {
        return label;
    };

    getLogo = (): string => {
        const labelLogo = 'label.logo';
        const labelMobileLogo = 'label.mobile.logo';
        this.portalLogo = this.languageService.getLabel(this.screenWidth > 480 ? labelLogo : labelMobileLogo);
        return (this.portalLogo === labelLogo || this.portalLogo === labelMobileLogo) ? '' : this.portalLogo;
    };

    getBackgroundColor = (): string => {
        return this.languageService.getLabel('label.css.main.header.background');
    };

    getHeaderTextColor = (): string => {
        return this.languageService.getLabel('label.css.main.header.text');
    };

    getLabelColor = (): string => {
        return this.languageService.getLabel('label.css.link');
    };

    getHoverColor = (): string => {
        return this.languageService.getLabel('label.css.popup.menu.selection.background');
    };

    navigateToHome = () => {
        void this.router.navigateByUrl('/dashboard');
    };

    protected isExternal() {
        const routes = [
            'external-registration',
            'external-reset-password'
        ];

        return routes.some(route => this.router.url.includes(route));
    }

    protected onMenuItemSelected(): void {
        if (this.sidenavMode === 'over') {
            void this.sidenav.close();
        }
    }
}
